import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL, formatDate } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { Badge } from "./ui/badge";
import { HandCoins, Loader2, Pencil, Trash2, X, Landmark, Wallet } from "lucide-react";

const bugunStr = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" });
const KATEGORILER = ["Vergi/SGK", "Tedarikçi", "Kira", "Kredi/Finansman", "Personel", "Diğer"];
const BOS = { tarih: "", kategori: "", kategori_detay: "", alici: "", tutar: "", hesap: "Banka", aciklama: "" };

export const Odemeler = ({ yil, ay, onChanged }) => {
  const [data, setData] = useState(null);
  const [tedarikciler, setTedarikciler] = useState([]);
  const [form, setForm] = useState({ ...BOS, tarih: bugunStr() });
  const [duzenleId, setDuzenleId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [odeme, ted] = await Promise.all([
        api.get("/odemeler", { params: { yil, ay } }),
        api.get("/tedarikciler"),
      ]);
      setData(odeme.data);
      setTedarikciler(ted.data || []);
    } catch (err) {
      toast.error(getErrMsg(err, "Ödemeler yüklenemedi."));
    }
  }, [yil, ay]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const kaydet = async () => {
    if (!form.tarih) return toast.error("Tarih zorunludur.");
    if (!form.kategori) return toast.error("Ödeme türü seçin.");
    if (form.kategori === "Diğer" && !form.kategori_detay.trim()) return toast.error("\"Diğer\" için ödeme türünü yazın.");
    if (!form.alici.trim()) return toast.error("Alıcı / kurum zorunludur.");
    const tutar = parseFloat(form.tutar);
    if (!tutar || tutar <= 0) return toast.error("Tutar 0'dan büyük olmalıdır.");
    setSaving(true);
    try {
      const govde = {
        tarih: form.tarih,
        kategori: form.kategori,
        kategori_detay: form.kategori === "Diğer" ? form.kategori_detay.trim() : "",
        alici: form.alici.trim(),
        tutar,
        hesap: form.hesap,
        aciklama: form.aciklama.trim(),
      };
      if (duzenleId) {
        await api.put(`/odemeler/${duzenleId}`, govde);
        toast.success("Ödeme güncellendi.");
      } else {
        await api.post("/odemeler", govde);
        toast.success("Ödeme kaydedildi.");
      }
      setDuzenleId(null);
      setForm({ ...BOS, tarih: bugunStr() });
      fetchData();
      onChanged?.();
    } catch (err) {
      toast.error(getErrMsg(err, "Ödeme kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  const duzenle = (o) => {
    setDuzenleId(o.id);
    setForm({
      tarih: o.tarih,
      kategori: o.kategori || "Diğer",
      kategori_detay: o.kategori_detay || "",
      alici: o.alici || o.tedarikci || "",
      tutar: String(o.tutar),
      hesap: o.hesap || "Banka",
      aciklama: o.aciklama || "",
    });
  };

  const sil = async (id) => {
    try {
      await api.delete(`/odemeler/${id}`);
      toast.success("Ödeme silindi.");
      fetchData();
      onChanged?.();
    } catch (err) {
      toast.error(getErrMsg(err, "Silinemedi."));
    }
  };

  const turEtiket = (o) => (o.kategori_detay || "").trim() || o.kategori || "—";

  return (
    <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="odemeler-bolumu">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-display text-sm font-semibold flex items-center gap-2">
            <HandCoins className="h-4 w-4 text-amber-600" /> Ödemeler
          </h3>
          <p className="text-[11px] text-muted-foreground">Vergi, tedarikçi, kira vb. — bütçeden bağımsız, yalnızca nakit akışına yansır</p>
        </div>
        {data && (
          <p className="text-xs text-muted-foreground">
            Bu dönem: <strong className="text-foreground tabular-nums" data-testid="odemeler-toplam">{formatTL(data.toplam)}</strong> · {data.adet} ödeme
          </p>
        )}
      </div>

      {data && data.kategoriler?.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="odemeler-kategori-ozet">
          {data.kategoriler.map((k) => (
            <Badge key={k.kategori} variant="secondary" className="font-normal tabular-nums">
              {k.kategori}: {formatTL(k.toplam)}
            </Badge>
          ))}
        </div>
      )}

      <div className={`rounded-lg border p-3 grid grid-cols-2 lg:grid-cols-7 gap-2 items-end ${duzenleId ? "border-sky-300 bg-sky-50/40" : ""}`} data-testid="odeme-form">
        <div className="space-y-1">
          <Label className="text-xs">Tarih *</Label>
          <Input type="date" value={form.tarih} onChange={(e) => set("tarih", e.target.value)} className="h-10" data-testid="odeme-tarih-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Ödeme Türü *</Label>
          <Select value={form.kategori} onValueChange={(v) => set("kategori", v)}>
            <SelectTrigger className="h-10" data-testid="odeme-kategori-select">
              <SelectValue placeholder="Seçin" />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {KATEGORILER.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {form.kategori === "Diğer" && (
          <div className="space-y-1">
            <Label className="text-xs">Tür Adı *</Label>
            <Input placeholder="Örn: Ceza, Bağış" value={form.kategori_detay} onChange={(e) => set("kategori_detay", e.target.value)} className="h-10" data-testid="odeme-kategori-detay-input" />
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">Alıcı / Kurum *</Label>
          <Input
            list={form.kategori === "Tedarikçi" ? "odeme-tedarikci-listesi" : undefined}
            placeholder={form.kategori === "Vergi/SGK" ? "Örn: Vergi Dairesi, SGK" : "Yazın"}
            value={form.alici} onChange={(e) => set("alici", e.target.value)}
            className="h-10" data-testid="odeme-alici-input"
          />
          <datalist id="odeme-tedarikci-listesi">
            {tedarikciler.map((t) => <option key={t.id} value={t.ad} />)}
          </datalist>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tutar (₺) *</Label>
          <Input type="number" inputMode="decimal" step="0.01" min="0" placeholder="0,00" value={form.tutar} onChange={(e) => set("tutar", e.target.value)} className="h-10 text-right tabular-nums" data-testid="odeme-tutar-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Ödendiği Yer *</Label>
          <Select value={form.hesap} onValueChange={(v) => set("hesap", v)}>
            <SelectTrigger className="h-10" data-testid="odeme-hesap-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="Banka">Banka</SelectItem>
              <SelectItem value="Kasa">Kasa</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Açıklama</Label>
          <Input placeholder="Opsiyonel" value={form.aciklama} onChange={(e) => set("aciklama", e.target.value)} className="h-10" data-testid="odeme-aciklama-input" />
        </div>
        <div className="flex gap-1.5">
          <Button onClick={kaydet} disabled={saving} className="h-10 flex-1 gap-1.5" data-testid="odeme-kaydet-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <HandCoins className="h-4 w-4" />}
            {duzenleId ? "Güncelle" : "Ödeme Ekle"}
          </Button>
          {duzenleId && (
            <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => { setDuzenleId(null); setForm({ ...BOS, tarih: bugunStr() }); }} data-testid="odeme-iptal-button" aria-label="Düzenlemeyi iptal et">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {!data ? (
        <Skeleton className="h-24" />
      ) : data.kayitlar.length === 0 ? (
        <p className="text-sm text-muted-foreground py-3 text-center" data-testid="odeme-bos">Bu dönemde ödeme yok.</p>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2">Tarih</th>
                <th className="px-3 py-2">Tür</th>
                <th className="px-3 py-2">Alıcı / Kurum</th>
                <th className="px-3 py-2">Yer</th>
                <th className="px-3 py-2">Açıklama</th>
                <th className="px-3 py-2 text-right">Tutar</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody data-testid="odeme-liste">
              {data.kayitlar.map((o) => (
                <tr key={o.id} className="border-t border-border/50">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(o.tarih)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Badge variant="outline" className="font-normal">{turEtiket(o)}</Badge>
                  </td>
                  <td className="px-3 py-2 font-medium">{o.alici || o.tedarikci || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      {o.hesap === "Kasa" ? <Wallet className="h-3 w-3" /> : <Landmark className="h-3 w-3" />}
                      {o.hesap || "Banka"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground max-w-[200px] truncate">{o.aciklama || "—"}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap">{formatTL(o.tutar)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => duzenle(o)} data-testid={`odeme-duzenle-${o.id}`} aria-label="Ödemeyi düzenle">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => sil(o.id)} data-testid={`odeme-sil-${o.id}`} aria-label="Ödemeyi sil">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
